// src/components/RoleMatrix.jsx
//
// "ROLE MATRIX" reference page (Account > Role Matrix). Read-only --
// this is the same table from Role-Matrix-v4.docx (delivered earlier)
// and the same table src/utils/permissions.js enforces in code,
// rendered in-app so an Admin/Super Admin can check what each tier can
// do without digging up the Word doc. There's nothing to edit here on
// purpose: the four tiers and their permissions are a fixed matrix,
// not per-row data -- assigning a TIER to a given user is what the
// separate Manage Roles page does.
//
// Keep this table's text in sync with MATRIX_ROWS below AND with
// PERMISSIONS in src/utils/permissions.js (which is what's actually
// enforced) AND with the backend's PERMISSION_MATRIX comment in
// app.py if any of the three ever change -- this page is a display
// copy, not the source of truth the app reads at runtime.
import React from "react";

const TIER_COLUMNS = ["View", "Contributor", "Admin", "Super Admin"];

const MATRIX_ROWS = [
  { module: "Dashboard", cells: ["View – All", "View – Own", "View – All", "View – All"] },
  { module: "Project", cells: ["View – All", "Add & edit own", "Add-Edit-Delete", "Add-Edit-Delete"] },
  { module: "Cost Estimation wizard", cells: ["View – All estimations", "Add & edit own", "Add-Edit-Delete", "Add-Edit-Delete"] },
  { module: "Rate Card (global — not OpCo-scoped)", cells: ["Not visible", "View only + Add Rate and Main Element", "Add-Edit-Delete", "Add-Edit-Delete"] },
  { module: "AI Assistant", cells: ["Available (All)", "Available (limited to own data)", "Available – Full", "Available – Full"] },
  { module: "Master (Role / Department / Company)", cells: ["Not visible", "Not visible", "Add-Edit", "Add-Edit-Delete"] },
  { module: "Account → Users (incl. OpCo Access field)", cells: ["Not visible", "Not visible", "Add-Edit", "Add-Edit-Delete"] },
  { module: "Configuration", cells: ["Not visible", "Not visible", "Not visible", "Full"] },
  { module: "Help", cells: ["Available", "Available", "Available", "Available"] },
];

// Cells reading "Not visible" get a muted look instead of the same
// weight as a real permission -- makes it easy to scan a tier's column
// and immediately see what's actually withheld from it.
function isWithheld(text) {
  return text === "Not visible";
}

export default function RoleMatrix() {
  return (
    <>
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between page-title-head my-3 my-sm-0">
        <div className="w-100 w-sm-auto mb-2 mb-sm-0">
          {/* Per request, this is now a breadcrumb trail ("Cost
              Estimation / {page}"), same pattern and color (#02bc9c,
              16px) as the Cost Estimation wizard's Step1-7 breadcrumbs
              -- replaces the earlier #788CA5 bordered/uppercase title
              treatment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              Role Matrix
            </span>
          </nav>
        </div>
      </div>

      <p className="text-muted mb-3">
        What each permission tier can do, module by module. To change which tier a
        specific user has, use Account → Manage Roles instead — this page is a
        reference, not an editor.
      </p>

      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              {/* role-matrix-table (new, scoped) -- per request, same row/
                  hover treatment as RateCard.jsx/ProjectList.jsx/UsersList.jsx/
                  EmailConfiguration.jsx/ManageRoles.jsx/Notifications.jsx:
                  row background #f2f2f2 @ 38% Transparency, hover #02bc9c
                  @ 95% Transparency, via themeOverrides.css. */}
              <table className="align-middle mb-0 table table-custom table-hover role-matrix-table w-100">
                {/* Per request, the solid dark-teal (#124d52) header fill
                    is removed -- header cells now use the same plain
                    bg-light look as the other tables (ManageRoles.jsx,
                    EmailConfiguration.jsx's non-Email-Template tables,
                    etc.) instead of a custom hardcoded color. */}
                <thead className="bg-light border-bottom border-top">
                  <tr className="text-dark fw-bold">
                    <th className="px-3 py-3 text-start">
                      Module
                    </th>
                    {TIER_COLUMNS.map((tier) => (
                      <th key={tier} className="px-3 py-3 text-start">
                        {tier}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MATRIX_ROWS.map((row) => (
                    <tr key={row.module}>
                      <td className="px-3 fw-semibold text-start">{row.module}</td>
                      {row.cells.map((cell, i) => (
                        <td key={i} className={`px-3 text-start${isWithheld(cell) ? " text-muted fst-italic" : ""}`}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
